import {
  Gesture,
  GestureDetector,
  GestureStateChangeEvent,
  GestureUpdateEvent,
  LongPressGestureHandlerEventPayload,
  PanGestureHandlerEventPayload,
  PinchGestureHandlerEventPayload,
  RotationGestureHandlerEventPayload,
  TapGestureHandlerEventPayload,
} from 'react-native-gesture-handler';
import { DimensionValue, LayoutChangeEvent, View, ViewStyle } from 'react-native';
import { memo, ReactNode } from 'react';
import { StyleProp } from 'react-native/Libraries/StyleSheet/StyleSheet';
import { HoverGestureHandlerEventPayload } from 'react-native-gesture-handler/lib/typescript/handlers/gestures/hoverGesture';
import { styles } from './GestureArea.styles';

export type GestureAreaProps = {
  listenerAreaProps?: { width: DimensionValue; height: DimensionValue };
  disabled?: boolean;
  children?: ReactNode;
  style?: StyleProp<ViewStyle>;

  onPanStart?: (event: GestureStateChangeEvent<PanGestureHandlerEventPayload>) => void;
  onPan?: (event: GestureUpdateEvent<PanGestureHandlerEventPayload>) => void;
  onPanEnd?: (event: GestureStateChangeEvent<PanGestureHandlerEventPayload>) => void;
  onPanFinalize?: (event: GestureStateChangeEvent<PanGestureHandlerEventPayload>) => void;
  onPinch?: (event: GestureUpdateEvent<PinchGestureHandlerEventPayload>) => void;
  onPinchEnd?: (event: GestureStateChangeEvent<PinchGestureHandlerEventPayload>) => void;
  onRotate?: (event: GestureUpdateEvent<RotationGestureHandlerEventPayload>) => void;
  onTap?: (event: GestureStateChangeEvent<TapGestureHandlerEventPayload>) => void;
  onLongPress?: (event: GestureStateChangeEvent<LongPressGestureHandlerEventPayload>) => void;
  onHoverStart?: (event: GestureStateChangeEvent<HoverGestureHandlerEventPayload>) => void;
  onHoverUpdate?: (event: GestureUpdateEvent<HoverGestureHandlerEventPayload>) => void;
  onHoverEnd?: (event: GestureStateChangeEvent<HoverGestureHandlerEventPayload>) => void;

  panActivateAfterLongPressMs?: number;

  onLayout?: (event: LayoutChangeEvent) => void;
};

const GestureArea = ({
  listenerAreaProps = { width: '100%', height: '100%' },
  disabled = false,
  onPanStart = () => {},
  onPan = () => {},
  onPanEnd = () => {},
  onPanFinalize = () => {},
  onPinch = () => {},
  onPinchEnd = () => {},
  onRotate = () => {},
  onTap = () => {},
  onLongPress = () => {},
  onHoverStart = () => {},
  onHoverUpdate = () => {},
  onHoverEnd = () => {},
  panActivateAfterLongPressMs = 0,
  onLayout = () => {},
  children,
  style,
}: GestureAreaProps) => {
  // console.log("GestureArea - rerender");

  /**
   * Gestures
   */
  const panGesture = Gesture.Pan()
    .minDistance(5)
    .runOnJS(true)
    .activateAfterLongPress(panActivateAfterLongPressMs)
    .onStart(event => {
      if (disabled) return;
      // console.log("GestureArea - Gesture.Pan - onStart: ", event);
      onPanStart(event);
    })
    .onUpdate(event => {
      if (disabled) return;
      // console.log("GestureArea - Gesture.Pan - onUpdate: ", event);
      onPan(event);
    })
    .onEnd(event => {
      if (disabled) return;
      // console.log("GestureArea - Gesture.Pan - onEnd: ", event);
      onPanEnd(event);
    })
    .onFinalize(event => {
      if (disabled) return;
      // console.log("GestureArea - Gesture.Pan - onFinalize: ", event);
      onPanFinalize(event);
    });

  const pinchGesture = Gesture.Pinch()
    .onUpdate(event => {
      if (disabled) return;
      // console.log("GestureArea - Gesture.Pinch - onUpdate: ", event);
      onPinch(event);
    })
    .onEnd(event => {
      if (disabled) return;
      // console.log("GestureArea - Gesture.Pinch - onEnd: ", event);
      onPinchEnd(event);
    });

  const rotateGesture = Gesture.Rotation().onUpdate(event => {
    if (disabled) return;
    // console.log("GestureArea - Gesture.Rotation - onUpdate: ", event);
    onRotate(event);
  });

  const tapGesture = Gesture.Tap().onEnd(event => {
    if (disabled) return;
    // console.log("GestureArea - Gesture.Tap - onEnd: ", event);
    onTap(event);
  });

  const longPressGesture = Gesture.LongPress()
    .minDuration(300)
    .maxDistance(5)
    .onStart(event => {
      if (disabled) return;
      // console.log("GestureArea - Gesture.LongPress - onStart: ", event);
      onLongPress(event);
    });

  const hoverGesture = Gesture.Hover()
    .onStart(event => {
      if (disabled) return;
      // console.log("GestureArea - Gesture.Hover - onStart: ", event);
      onHoverStart(event);
    })
    .onUpdate(event => {
      if (disabled) return;
      // console.log("GestureArea - Gesture.Hover - onUpdate: ", event);
      onHoverUpdate(event);
    })
    .onEnd(event => {
      if (disabled) return;
      // console.log("GestureArea - Gesture.Hover - onEnd: ", event);
      onHoverEnd(event);
    });

  // const composed = Gesture.Race(Gesture.Simultaneous(panGesture, pinchGesture, rotateGesture), Gesture.Exclusive(doubleTapGesture, tapGesture));
  const composed = Gesture.Simultaneous(
    Gesture.Race(
      Gesture.Simultaneous(longPressGesture, panGesture, pinchGesture, rotateGesture, hoverGesture),
      tapGesture,
    ),
    Gesture.Native(), // Questo permette alla ScrollView di ricevere eventi
  );

  return (
    <View onLayout={onLayout!} style={[style, listenerAreaProps, disabled && styles.disabled]} 
        pointerEvents={disabled ? 'none' : 'auto'}>
      <GestureDetector gesture={composed} enableContextMenu={false}>
        <View style={[styles.workingArea, disabled && styles.disabled]} 
        pointerEvents={disabled ? 'none' : 'auto'}>{children}</View>
      </GestureDetector>
    </View>
  );
};

export default memo(GestureArea);
